import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Check, Edit3, Plus, Search, Trash2, Users } from "lucide-react";
import api from "../../Services/api";
import MenuLateral from "../../Componentes/MenuLateral";
import MenuSuperior from "../../Componentes/MenuSuperior";
import { useAuthContext } from "../../context/authContext";
import { useOperador } from "../../Hooks/useOperador";
import { useNavigate } from "react-router-dom";
import "./index.css";

const emptyForm = {
  nome: "",
  descricao: "",
  operadores: [],
};

const CadAgendaGrupo = () => {
  const { authReady } = useAuthContext();
  const { IDOperador, nome: nomeOperador, operador } = useOperador();
  const navigate = useNavigate();
  const [grupos, setGrupos] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const idFormatado = String(IDOperador || "").padStart(6, "0");

  useEffect(() => {
    if (!operador && !authReady) navigate("/");
  }, [operador, authReady, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [gruposResponse, operadoresResponse] = await Promise.all([
        api.get("/agenda-grupos"),
        api.get("/operadores"),
      ]);
      setGrupos(Array.isArray(gruposResponse.data) ? gruposResponse.data : []);
      setOperadores(Array.isArray(operadoresResponse.data) ? operadoresResponse.data : []);
    } catch (err) {
      console.error(err);
      Swal.fire("Erro", "Nao foi possivel carregar os grupos da agenda.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredOperators = useMemo(() => {
    const termo = search.trim().toLowerCase();
    return operadores.filter((op) => !termo || String(op.nome || "").toLowerCase().includes(termo));
  }, [operadores, search]);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const toggleOperador = (id) => {
    const numericId = Number(id);
    setForm((prev) => ({
      ...prev,
      operadores: prev.operadores.includes(numericId)
        ? prev.operadores.filter((opId) => opId !== numericId)
        : [...prev.operadores, numericId],
    }));
  };

  const editGroup = (grupo) => {
    setEditingId(grupo.IDAgendaGrupo);
    setForm({
      nome: grupo.nome || "",
      descricao: grupo.descricao || "",
      operadores: (grupo.operadores || []).map(Number),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSearch("");
  };

  const saveGroup = async () => {
    if (!form.nome.trim()) {
      Swal.fire("Atencao", "Informe o nome do grupo.", "warning");
      return;
    }
    if (form.operadores.length === 0) {
      Swal.fire("Atencao", "Selecione pelo menos um operador.", "warning");
      return;
    }

    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao.trim() || null,
        operadores: form.operadores,
        situacao: 1,
      };

      if (editingId) {
        await api.put(`/agenda-grupo/${editingId}`, payload);
      } else {
        await api.post("/agenda-grupo", payload);
      }

      Swal.fire("Sucesso", "Grupo da agenda salvo com sucesso.", "success");
      resetForm();
      await loadData();
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.errors?.default || "Nao foi possivel salvar o grupo.";
      Swal.fire("Erro", message, "error");
    }
  };

  const deleteGroup = async (grupo) => {
    const result = await Swal.fire({
      title: "Excluir grupo?",
      text: grupo.nome,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Excluir",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/agenda-grupo/${grupo.IDAgendaGrupo}`);
      Swal.fire("Sucesso", "Grupo excluido com sucesso.", "success");
      await loadData();
    } catch (err) {
      console.error(err);
      Swal.fire("Erro", "Nao foi possivel excluir o grupo.", "error");
    }
  };

  return (
    <div className="menu-container-menu">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <div className="page-shell agenda-group-page">
          <MenuSuperior userId={idFormatado} userName={nomeOperador} />

          <section className="agenda-group-header">
            <span><Users size={17} /> Agenda</span>
            <h1>Grupos de operadores</h1>
          </section>

          <section className="agenda-group-form">
            <div className="agenda-group-fields">
              <label>
                Nome do grupo
                <input value={form.nome} onChange={(e) => update("nome", e.target.value)} />
              </label>
              <label>
                Descricao
                <textarea value={form.descricao} onChange={(e) => update("descricao", e.target.value)} rows={3} />
              </label>
            </div>

            <div className="agenda-group-operators">
              <label className="agenda-group-search">
                <Search size={16} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar operador" />
              </label>
              <div className="agenda-group-operator-list">
                {filteredOperators.map((op) => {
                  const selected = form.operadores.includes(Number(op.IDOperador));
                  return (
                    <button
                      type="button"
                      key={op.IDOperador}
                      className={selected ? "selected" : ""}
                      onClick={() => toggleOperador(op.IDOperador)}
                    >
                      <span>{String(op.nome || "?").charAt(0).toUpperCase()}</span>
                      <strong>{op.nome}</strong>
                      {selected ? <Check size={16} /> : <Plus size={16} />}
                    </button>
                  );
                })}
              </div>
            </div>

            <footer>
              {editingId && <button type="button" className="agenda-group-secondary" onClick={resetForm}>Cancelar edicao</button>}
              <button type="button" className="agenda-group-primary" onClick={saveGroup}>
                <Check size={17} /> Salvar grupo
              </button>
            </footer>
          </section>

          <section className="agenda-group-list">
            {loading && <p>Carregando grupos...</p>}
            {!loading && grupos.length === 0 && <p>Nenhum grupo cadastrado.</p>}
            {!loading && grupos.map((grupo) => (
              <article key={grupo.IDAgendaGrupo}>
                <div>
                  <h3>{grupo.nome}</h3>
                  <p>{grupo.descricao || "Sem descricao"}</p>
                  <small>{(grupo.operadores || []).length} operadores</small>
                </div>
                <div className="agenda-group-actions">
                  <button type="button" onClick={() => editGroup(grupo)} title="Editar" aria-label="Editar">
                    <Edit3 size={17} />
                  </button>
                  <button type="button" onClick={() => deleteGroup(grupo)} title="Excluir" aria-label="Excluir">
                    <Trash2 size={17} />
                  </button>
                </div>
              </article>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
};

export default CadAgendaGrupo;
